import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gallery1 from "../assets/images/gallery1.jpeg";
import { FaImages, FaPlayCircle } from "react-icons/fa";

const OurGallery = () => {
  const [visibleImages, setVisibleImages] = useState(6);
  const [gallery, setGallery] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/get_gallery`);
        const data = await response.json();
        setGallery(data.data || []);
      } catch (error) {
        console.error("Error fetching Gallery:", error);
      }
    };

    fetchGallery();
  }, []);

  const loadMore = () => {
    setVisibleImages((prev) => Math.min(prev + 6, gallery.length));
  };

  const displayedImages = gallery.slice(0, visibleImages);

  return (
    <div className="min-h-screen bg-[#EDE9E0] py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#8F8431] flex items-center justify-center">
              <FaImages className="text-white text-3xl" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#2C3A2A]">Our Gallery</h1>
          </div>
          <p className="text-[#5F6A5A] text-lg max-w-xl mx-auto">
            A visual journey through our projects and moments
          </p>
        </div>

        {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayedImages.length > 0 ? (
                displayedImages.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -12 }}
                    onClick={() => setSelectedImage(item)}
                    className="group bg-[#EDE9E0] rounded-3xl overflow-hidden shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0] cursor-pointer"
                  >
                    <div className="relative h-80 overflow-hidden">
                      {item.type === 'video' ? (
                        <>
                          <video
                            src={item?.photo || gallery1}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                            <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center">
                              <FaPlayCircle className="w-10 h-10 text-[#8F8431]" />
                            </div>
                          </div>
                        </>
                      ) : (
                        <img
                          src={item?.photo || gallery1}
                          alt={item?.title || "Gallery Image"}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                      )}
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end p-6">
                        <div>
                          <p className="text-white text-lg font-medium">{item?.title || "Project View"}</p>
                          <p className="text-[#C7C091] text-sm mt-1 capitalize">{item?.type || "Image"}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-24 h-24 rounded-full bg-[#EDE9E0] shadow-[inset_8px_8px_16px_#d4c9b8,inset_-8px_-8px_16px_#ffffff] flex items-center justify-center mb-6">
                    <FaImages className="text-5xl text-[#8F8431]/40" />
                  </div>
                  <p className="text-2xl text-[#5F6A5A]">No items available yet</p>
                </div>
              )}
            </div>

        {/* Load More Button */}
        {visibleImages < gallery.length && (
          <div className="text-center mt-16">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={loadMore}
              className="px-10 py-4 rounded-2xl font-semibold text-lg bg-[#8F8431] text-white shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] active:shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E] transition-all flex items-center gap-3 mx-auto"
            >
              Load More Images
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.button>
          </div>
        )}
      </div>

      {/* Media Preview Modal */}
        <AnimatePresence>
          {selectedImage && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setSelectedImage(null)}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-[#EDE9E0] max-w-4xl w-full rounded-3xl overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative">
                  {selectedImage.type === 'video' ? (
                    <video
                      src={selectedImage.photo || gallery1}
                      controls
                      className="w-full max-h-[85vh] object-contain"
                    />
                  ) : (
                    <img
                      src={selectedImage.photo || gallery1}
                      alt={selectedImage.title}
                      className="w-full max-h-[85vh] object-contain"
                    />
                  )}
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute top-4 right-4 bg-white/90 hover:bg-white text-[#2C3A2A] p-3 rounded-2xl shadow-lg"
                  >
                    ✕
                  </button>
                </div>
                {selectedImage.title && (
                  <div className="p-6 text-center">
                    <h3 className="text-2xl font-semibold text-[#2C3A2A]">{selectedImage.title}</h3>
                    <p className="text-[#5F6A5A] mt-1 capitalize">{selectedImage.type || "Image"}</p>
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
    </div>
  );
};

export default OurGallery;